import { get, uniqBy } from 'lodash';
import { useCallback } from 'react';
import { getActions, getValueBinding } from '../shared';
import { useChatComponentContext } from './context';
import { ChatComponentBindingValue, ChatComponentProps } from './type';
import { DEFAULT_PAGINATION, resolveUploadFileSupport } from './utils';

export const useChatComponent = (props: ChatComponentProps) => {
  const {
    chatActions,
    onPress,
    record,
    data,
    isInCustomList,
    id,
    initId,
    attributes,
    changeInput,
  } = props;
  const context = useChatComponentContext();
  const bindingValue = getValueBinding(
    isInCustomList ? initId : id,
    isInCustomList ? record : data,
    props
  ) as ChatComponentBindingValue;
  const sendPushNotification = {
    enabled: get(bindingValue, 'sendNotification.enabled', false),
    title: get(bindingValue, 'sendNotification.title', ''),
  };
  const usernameFieldFid = attributes.chatSetting?.usernameField?.fid;
  // MIK-4828: always / never / conditional. `conditional` reads a Boolean
  // "disable upload" field off the chatroom currently open, which the group
  // list request below asks the server for.
  const { supportUploadFile, uploadFileDisabledFieldFid } =
    resolveUploadFileSupport({
      attributes,
      selectedGroup: context.selectedGroup,
      groupMessages: context.groupMessages,
    });

  const fetchUnreadStatus = async (groupIds: string[]) => {
    const data = await chatActions?.getUnreadStatus(groupIds);
    const unreadMessageStatus = data.reduce<Record<string, number>>(
      (prev, curr) => {
        prev[curr.groupId] = curr.unreadCount;
        return prev;
      },
      {}
    );
    context.setUnreadMessageStatus(unreadMessageStatus);
  };

  const fetchGroupList = async () => {
    context.setGroupLoading(true);
    const data = await chatActions?.getGroupMessages({
      usernameFieldFid,
      uploadFileDisabledFieldFid,
    });
    fetchUnreadStatus(data.map((groupMessage) => groupMessage.id));
    context.setGroupMessages(data);
    context.setGroupLoading(false);
    return data;
  };

  const fetchMessageHistory = useCallback(
    async ({
      groupId,
      pagination,
    }: {
      groupId: string;
      pagination: { skip: number; limit: number };
    }) => {
      context.setMessageHistoryLoading(true);
      const data = await chatActions.getMessageHistories({
        groupId,
        pagination,
        usernameFieldFid,
      });
      const oldGroupId = context.selectedGroup?.id;
      if (oldGroupId !== groupId) {
        context.setMessageHistories(data.data);
        context.setMessageHistoriesPagination({
          limit: +data.limit,
          skip: +data.skip,
          total: +data.total,
        });
      } else {
        if (data.data.length) {
          context.setMessageHistories((old) => {
            // don't refresh when data is not updated
            if (
              !old.length ||
              data.data.find(
                (newMessage) =>
                  !old.find((oldMessage) => oldMessage.id === newMessage.id)
              )
            ) {
              const newMessages = uniqBy([...old, ...data.data], (e) => e.id);
              return newMessages;
            }
            return old;
          });
          context.setMessageHistoriesPagination({
            limit: +data.limit,
            skip: +data.skip,
            total: +data.total,
          });
          setTimeout(() => {
            // scroll to bottom little bit to support continue fetch loadmore
            context.listRef?.current?.scrollToOffset({
              offset: context.listScrollPosition.y + 10,
              animated: true,
            });
          }, 300);
        }
      }
      // perform read when there are unread messages
      if (context.unreadMessageStatus?.[groupId] && data.data.length) {
        props.chatActions.makeReadStatus({
          messageIds: data.data.map((message) => message.id),
        });
        context.setUnreadMessageStatus((old) => {
          const newStatus = { ...old };
          delete newStatus[groupId];
          return newStatus;
        });
      }
      context.setMessageHistoryLoading(false);
      return data;
    },
    [
      context.selectedGroup?.id,
      context.unreadMessageStatus,
      context.listScrollPosition.y,
    ]
  );

  const fetchMessageHistoryNextPage = async () => {
    if (context.selectedGroup) {
      const paginationNext = {
        ...context.messageHistoriesPagination,
        skip:
          context.messageHistoriesPagination.skip +
          context.messageHistoriesPagination.limit,
      };
      fetchMessageHistory({
        groupId: context.selectedGroup?.id,
        pagination: paginationNext,
      });
    }
  };

  const sendMessage = async () => {
    if (
      !context.selectedGroup ||
      (!context.inputValue && !context.filesValue.length)
    ) {
      return;
    }
    context.setMessageSending(true);
    const newMessage = await chatActions.sendMessage({
      groupId: context.selectedGroup?.id,
      message: {
        text: context.inputValue,
        files: context.filesValue,
      },
      usernameFieldFid,
    });
    changeInput(context.inputValue || '');
    // require setTimeout to update redux (by binding action) before run action
    setTimeout(() => {
      onPress?.(getActions(props, 'action'), {
        record: { messageId: newMessage.id, sendPushNotification },
      });
    }, 1000);
    context.setInputValue('');
    context.setFilesValue([]);
    context.setMessageHistories((old) => [...old, newMessage]);
    context.setMessageSending(false);
    if (context.messageHistories.length) {
      setTimeout(() => {
        // scroll to end
        context.listRef?.current?.scrollToIndex({
          index: context.messageHistories.length - 1,
          animated: true,
        });
      }, 300);
    }
    fetchGroupList();
  };

  const onSelectGroup = async (groupId: string | null) => {
    if (!groupId) {
      context.setSelectedGroup(null);
      return;
    }
    const oldGroupId = context.selectedGroup?.id;
    if (oldGroupId === groupId) {
      return;
    }
    context.setSelectedGroup(groupId);
    context.setMessageHistories([]);
    const data = await fetchMessageHistory({
      groupId,
      pagination: DEFAULT_PAGINATION,
    });
    if (data.data.length) {
      setTimeout(() => {
        // scroll to end
        try {
          context.listRef?.current?.scrollToIndex({
            index: data.data.length - 1,
            animated: true,
          });
        } catch (e) {
          // nothing
        }
      }, 300);
    }
  };

  const displayNumberOfUser = ({
    numberOfUsers,
  }: {
    numberOfUsers?: number;
  }) => {
    return numberOfUsers ? Number(numberOfUsers) !== 2 : false;
  };

  return {
    fetchGroupList,
    fetchMessageHistory,
    sendMessage,
    onSelectGroup,
    fetchMessageHistoryNextPage,
    displayNumberOfUser,
    supportUploadFile,
  };
};

export type UseChatComponentProps = ReturnType<typeof useChatComponent>;
